import {
  Alert,
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  EFSRestoreDestinationPerformanceModeEnum,
  EFSRestoreDestinationThroughputModeEnum,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useState, useMemo } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { AWSSubnetSelect } from '@/components/selectors/awsSubnetSelect';
import { SecurityGroupSelect } from '@/components/selectors/securityGroupSelect';
import { VpcSelect } from '@/components/selectors/vpcSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreEFSState } from '../restoreEfsWizard';

export const EFSConfiguration = (props: StepProps<RestoreEFSState>) => {
  const dal = useDAL();

  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);

  // Source account is required for EFS restore (no cross-account support)
  const hasSourceAccount = !!props.wizardState.restoreAccount;

  // Build AWS Backup vault info for the snapshot toolbar (EFS snapshots are stored in AWS Backup)
  const efsProps = props.wizardState.snapshot.efsSnapshotProperties;
  const awsBackupVaultInfo = useMemo(() => {
    if (!efsProps) return undefined;
    return {
      cloudProvider: Provider.Aws,
      region: efsProps.region || props.wizardState.initialRegionName,
    };
  }, [efsProps, props.wizardState.initialRegionName]);

  // Get the selected subnet
  const selectedSubnet = props.wizardState.subnetIds[0];

  useEffect(() => {
    props.setWizardState((state) => ({
      ...state,
      securityGroupIds: [],
      subnetIds: [],
      vpc: undefined,
      regionName: state.initialRegionName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.restoreAccount]);

  // Auto-select restore account when accounts list becomes available
  useEffect(() => {
    if (
      !props.wizardState.restoreAccount &&
      props.wizardState.accounts?.length
    ) {
      props.setWizardState((state) => ({
        ...state,
        restoreAccount: state.accounts?.[0],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.accounts, props.wizardState.restoreAccount]);

  const canGoNext = () => {
    // Must have a restore account (source account for EFS)
    if (!hasSourceAccount) {
      return false;
    }

    // Must have at least one subnet selected
    const subnetValid = !!selectedSubnet;

    // Base validation
    return !(
      isRestoring ||
      !props.wizardState.regionName ||
      !props.wizardState.fileSystemName ||
      !subnetValid
    );
  };

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const tags = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon:restore': 'true',
    };

    return dal.restore.efs
      .restore(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          region: props.wizardState.regionName || '',
          fileSystemName: props.wizardState.fileSystemName || '',
          subnetIds: props.wizardState.subnetIds,
          securityGroupIds: props.wizardState.securityGroupIds,
          encryptionKey: getEncryptionKey(props.wizardState.encryption),
          tags,
          performanceMode: props.wizardState
            .performanceMode as EFSRestoreDestinationPerformanceModeEnum,
          throughputMode: props.wizardState
            .throughputMode as EFSRestoreDestinationThroughputModeEnum,
          provisionedThroughputMibps:
            props.wizardState.throughputMode === 'provisioned'
              ? props.wizardState.provisionedThroughputMibps
              : undefined,
        },
      })
      .then(() =>
        props.finish(
          `Started restore job for ${props.wizardState.fileSystemName}`
        )
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setIsRestoring(false);
        setError(`Failed to restore EFS file system: ${errorMessage}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      hideStepper
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
          awsBackupVaultInfo={awsBackupVaultInfo}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        {!hasSourceAccount && (
          <Alert severity='info' className='mb-[20px]'>
            <Typography variant='body2'>
              EFS backups are stored in AWS Backup Vault in the source account
              and can only be restored to the same account.
            </Typography>
          </Alert>
        )}

        {/* File system name + VPC */}
        <Stack
          direction='row'
          alignItems='flex-start'
          gap='36px'
          className='mt-[20px]'
        >
          <FormControl size='small' className='flex-1'>
            <FormLabel>File system name</FormLabel>
            <TextField
              data-testid='fileSystemNameTextField'
              size='small'
              value={props.wizardState.fileSystemName || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  fileSystemName: event.target.value,
                }))
              }
              disabled={!hasSourceAccount}
            />
          </FormControl>
          <VpcSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            vpc={props.wizardState.vpc}
            onChange={(vpc) =>
              props.setWizardState((state) => ({
                ...state,
                vpc,
                subnetIds: [],
                securityGroupIds: [],
              }))
            }
            disabledItems={[]}
          />
        </Stack>

        {/* Security Groups + Subnet */}
        <Stack
          direction='row'
          alignItems='flex-start'
          gap='36px'
          className='mt-[20px]'
        >
          <SecurityGroupSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            securityGroupIds={props.wizardState.securityGroupIds}
            vpc={props.wizardState.vpc || ''}
            isSubnetIdMandatory={false}
            isOptionalField={true}
            onChange={(securityGroupIds) =>
              props.setWizardState((state) => ({
                ...state,
                securityGroupIds: securityGroupIds ?? [],
              }))
            }
          />
          <FormControl size='small' className='flex-1'>
            <FormLabel>Subnet</FormLabel>
            <AWSSubnetSelect
              accountId={props.wizardState.restoreAccount?.id}
              regionName={props.wizardState.regionName}
              subnetId={selectedSubnet}
              vpcScope={props.wizardState.vpc || ''}
              hideLabel
              onChange={(subnetId) =>
                props.setWizardState((state) => ({
                  ...state,
                  subnetIds: subnetId ? [subnetId] : [],
                }))
              }
            />
          </FormControl>
        </Stack>

        {/* Performance and Throughput modes */}
        <Stack
          direction='row'
          alignItems='flex-start'
          gap='36px'
          className='mt-[20px]'
        >
          <FormControl size='small' className='flex-1'>
            <FormLabel>Performance mode (optional)</FormLabel>
            <Select
              data-testid='performanceModeSelect'
              size='small'
              value={props.wizardState.performanceMode || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  performanceMode: event.target.value || undefined,
                }))
              }
              displayEmpty
              disabled={!hasSourceAccount}
            >
              <MenuItem value=''>
                <em>Use original</em>
              </MenuItem>
              <MenuItem value='generalPurpose'>General Purpose</MenuItem>
              <MenuItem value='maxIO'>Max I/O</MenuItem>
            </Select>
          </FormControl>
          <FormControl size='small' className='flex-1'>
            <FormLabel>Throughput mode (optional)</FormLabel>
            <Select
              data-testid='throughputModeSelect'
              size='small'
              value={props.wizardState.throughputMode || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  throughputMode: event.target.value || undefined,
                  // Clear provisioned throughput if not provisioned mode
                  provisionedThroughputMibps:
                    event.target.value === 'provisioned'
                      ? state.provisionedThroughputMibps
                      : undefined,
                }))
              }
              displayEmpty
              disabled={!hasSourceAccount}
            >
              <MenuItem value=''>
                <em>Use original</em>
              </MenuItem>
              <MenuItem value='bursting'>Bursting</MenuItem>
              <MenuItem value='provisioned'>Provisioned</MenuItem>
              <MenuItem value='elastic'>Elastic</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* Provisioned throughput (only shown when throughput mode is provisioned) */}
        {props.wizardState.throughputMode === 'provisioned' && (
          <Stack
            direction='row'
            alignItems='flex-start'
            gap='36px'
            className='mt-[20px]'
          >
            <FormControl size='small' className='flex-1'>
              <FormLabel>Provisioned throughput (MiB/s)</FormLabel>
              <TextField
                data-testid='provisionedThroughputTextField'
                size='small'
                type='number'
                value={props.wizardState.provisionedThroughputMibps || ''}
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    provisionedThroughputMibps: event.target.value
                      ? parseFloat(event.target.value)
                      : undefined,
                  }))
                }
                inputProps={{ min: 1, step: 0.1 }}
                disabled={!hasSourceAccount}
              />
            </FormControl>
            <Box className='flex-1' /> {/* Spacer for alignment */}
          </Stack>
        )}
      </Box>

      <Divider />
      <CardContent className='px-0'>
        {props.wizardState.regionName && hasSourceAccount && (
          <EncryptionComponent
            encryption={props.wizardState.encryption}
            accountId={props.wizardState.restoreAccount!.id}
            regionName={props.wizardState.regionName}
            onChange={(encryption) =>
              props.setWizardState((state) => ({
                ...state,
                encryption,
              }))
            }
          />
        )}
      </CardContent>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
